// apps/client/src/pages/settings/components/UserQuotaDisplay.tsx

import { Box, Button, LinearProgress, Typography } from '@mui/material';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { getUserQuotaInfo } from '../../../utils/quota';

interface UserQuotaDisplayProps {
  userCount: number;
}

export const UserQuotaDisplay: React.FC<UserQuotaDisplayProps> = ({ userCount }) => {
  const { currentOrganisation } = useAuth();

  if (!currentOrganisation) return null;

  const quotaInfo = getUserQuotaInfo(currentOrganisation, userCount);

  // Don't show if unlimited
  if (quotaInfo.isUnlimited) return null;

  const percentage = (quotaInfo.current / quotaInfo.limit) * 100;
  const progressColor = quotaInfo.isAtLimit
    ? 'error'
    : quotaInfo.isNearLimit
      ? 'warning'
      : 'primary';

  return (
    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
      {(quotaInfo.isAtLimit || quotaInfo.isNearLimit) && (
        <Button
          component={RouterLink}
          to="/settings/plan"
          size="small"
          variant="outlined"
          color={quotaInfo.isAtLimit ? 'error' : 'warning'}
          sx={{
            whiteSpace: 'nowrap',
            fontSize: '0.7rem',
            padding: '2px 8px',
            minWidth: 'auto',
          }}
        >
          Upgrade to add more
        </Button>
      )}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 200 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="caption" fontWeight="medium">
            {quotaInfo.current}/{quotaInfo.limit} team members
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={Math.min(percentage, 100)}
          color={progressColor}
          sx={{
            height: 6,
            borderRadius: 1,
            backgroundColor: 'action.hover',
          }}
        />
      </Box>
    </Box>
  );
};

export default UserQuotaDisplay;
